import CustomDataTable from '@/components/organisms/CustomDataTable/CustomDataTable';
import {getCellDataMappedCols} from '@/helpers/dataTableCellDataMapper';
import {useRewardCatalog} from '@/hooks/useRewardCatalog';
import {useEffect, useMemo, useRef, useState, useCallback} from 'react';
import DownloadDigitalCodeReportModal from './DownloadDigitalCodeReportModal';
import {FileUploadWithModal} from '@/components/organisms';
import {useNavigate} from 'react-router-dom';
import {cellDataMapperObj} from './cellDataMapper';
import {
  REWARD_CATALOG_ROUTES,
  Constants,
  DISABLE_HEADERS,
  UPLOAD_TITLES,
  UPLOAD_ERROR_MESSAGES
} from '@/utils/constants/commonConstants';
import {convertToLowerCase} from '@/utils/commons';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useAppDispatch} from '@/store';
import {resetRewardsData} from '@/store/slices/rewardsSlice';
import PropTypes from 'prop-types';

export const RewardCatalogList = ({rewardIdFilter}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const tableRef = useRef(null);
  const [uploadFor, setUploadFor] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [rowSelected, setRowSelected] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [showDigitalCodeReportModal, setShowDigitalCodeReportModal] =
    useState(false);

  const {
    rewardCatalogData,
    rewardCatalogColConfigData,
    pageIndex,
    customRules,
    customRulesRewards,
    uploading,
    uploadProgress,
    uploadErrorMessage,
    formData,
    setFormData,
    openUpload,
    setOpenUpload = () => {},
    setPageIndex,
    setSortingObj,
    sortingObj,
    setSearchArry,
    setSearchArrImmediate,
    onDownloadBtnClick,
    isLoading,
    handleResetAll,
    dropdownList,
    handleDownloadTemplate,
    handleBulkUpload,
    setUploading,
    handleCloseNotification,
    handleCloseModal,
    downloadRewardReport
  } = useRewardCatalog();

  const normalizedRewardIds = useMemo(() => {
    if (!Array.isArray(rewardIdFilter) || rewardIdFilter.length === 0) {
      return [];
    }
    return rewardIdFilter
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
  }, [rewardIdFilter]);

  useEffect(() => {
    if (normalizedRewardIds.length === 0) return;

    // Replace any existing filters (explicit requirement):
    // reset table filters and then apply rewardIdList.
    if (tableRef.current?.handleResetAll) {
      tableRef.current.handleResetAll();
    } else {
      handleResetAll();
    }
    setPageIndex(0);

    setSearchArrImmediate({
      field: 'rewardIdList',
      value: JSON.stringify(normalizedRewardIds),
      type: 'array'
    });
  }, [
    normalizedRewardIds,
    handleResetAll,
    setPageIndex,
    setSearchArrImmediate
  ]);

  const handleNavigation = (data) => {
    // Reseting the state before moving to the screen
    dispatch(resetRewardsData());
    navigate(data);
  };

  const cellDataMappedCols = getCellDataMappedCols(
    cellDataMapperObj(handleNavigation, downloadRewardReport),
    rewardCatalogColConfigData
  );

  const onDownloadClick = async (selectedRows, selectionMeta = {}) => {
    // selectedRows is empty array when select all is active (passed from CustomDataTable)
    // Otherwise it contains the selected row objects
    const ids =
      selectedRows?.length > 0
        ? selectedRows.map((item) => item?.rewardId || item?.uuid)
        : [];
    const excludeIds = Array.isArray(selectionMeta?.excludeIds)
      ? selectionMeta.excludeIds
      : [];
    onDownloadBtnClick(ids, excludeIds);
  };

  const handleActionClick = (action) => {
    const displayText = convertToLowerCase(action?.displayText);
    switch (displayText) {
      case REWARD_CATALOG_ROUTES.NEW_REWARD:
        dispatch(resetRewardsData());
        navigate(ROUTES.REWARDS_CREATE);
        break;
      case REWARD_CATALOG_ROUTES.BULK_UPLOAD_REWARDS:
        setUploadFor(Constants.BulkRewards);
        setOpenUpload(true);
        break;
      case REWARD_CATALOG_ROUTES.BULK_UPLOAD_DIGITAL_CODES:
        setUploadFor(Constants.BulkDigitalCodes);
        setOpenUpload(true);
        break;
      case REWARD_CATALOG_ROUTES.DOWNLOAD_DIGITAL_CODE_REPORT:
        setShowDigitalCodeReportModal(true);
        break;
      case REWARD_CATALOG_ROUTES.DOWNLOAD:
        onDownloadClick();
        break;
      default:
        break;
    }
  };

  const handleRowSelect = useCallback((selectedRecords, isAll) => {
    setRowSelected(selectedRecords);
    setIsSelectAll(isAll);
  }, []);

  return (
    <div className='rewardCatalogDataTable w-full'>
      <CustomDataTable
        ref={tableRef}
        columns={cellDataMappedCols}
        records={rewardCatalogData?.data || []}
        isLoading={isLoading}
        pageSize={10}
        totalRecords={rewardCatalogData?.totalRecords}
        paginationConfig={{
          pageIndex,
          setPageIndex
        }}
        sortingConfig={{
          handleSorting: setSortingObj,
          initialSortConfig: sortingObj
        }}
        filterConfig={{
          setSearchArry,
          onResetAll: handleResetAll,
          disabledHeaders: DISABLE_HEADERS.REWARD_CATALOG_HEADERS
        }}
        selectionConfig={{
          onRowSelect: handleRowSelect,
          isSelectAll,
          globalSelectionEnable: true
        }}
        toolbarConfig={{
          onDownload: onDownloadClick,
          actionItems: dropdownList,
          showActionMenu: true,
          onActionClick: handleActionClick
        }}
      />
      {rewardCatalogData?.totalRecords > 0 && (
        <div className='txtRightAlign'>
          {rewardCatalogData?.totalRecords} total items
        </div>
      )}
      <DownloadDigitalCodeReportModal
        showModal={showDigitalCodeReportModal}
        onClose={() => setShowDigitalCodeReportModal(false)}
      />
      <FileUploadWithModal
        title={
          uploadFor === Constants.BulkDigitalCodes
            ? UPLOAD_TITLES.UPLOAD_DIGITAL_CODES_LIST
            : UPLOAD_TITLES.UPLOAD_REWARDS_LIST
        }
        fileType='csv'
        uploadFor={uploadFor}
        openUpload={openUpload}
        uploading={uploading}
        setUploading={setUploading}
        uploadProgress={uploadProgress}
        uploadErrorMessage={uploadErrorMessage}
        setOpenUpload={setOpenUpload}
        setFormData={setFormData}
        formData={formData}
        customErrorMessages={{
          wrongFormat: UPLOAD_ERROR_MESSAGES.WRONG_FORMAT
        }}
        showDownloadTemplate={true}
        handleDownloadTemplate={handleDownloadTemplate}
        customRules={
          uploadFor === Constants.BulkDigitalCodes
            ? customRules
            : customRulesRewards
        }
        isCsvValidationRequired={true}
        primaryButtonText='Upload'
        subTitle=' '
        isPending={false}
        onClickPrimary={() => {
          handleBulkUpload(formData, uploadFor);
        }}
        primaryButtonDisabled={!formData?.storeList}
        handleCloseNotification={handleCloseNotification}
        handleCloseModal={handleCloseModal}
      />
    </div>
  );
};

RewardCatalogList.propTypes = {
  rewardIdFilter: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  )
};
